import React, { useState, useEffect, useCallback } from 'react';
import { employeeV2Service } from '../../../services/api';
import {
    C, fmtMoney, fmtDate,
    cardStyle, cardHeaderStyle, cardTitleStyle, labelStyle, valueStyle,
} from './shared';

const EMPLOYMENT_TYPES = [
    { value: 'full_time', key: 'full_time_label' },
    { value: 'part_time', key: 'part_time_label' },
    { value: 'contractor', key: 'contractor' },
];
const EMPLOYMENT_TYPE_KEYS = { full_time: 'full_time_label', part_time: 'part_time_label', contractor: 'contractor' };

const PAY_TYPES = [
    { value: 'salary', key: 'pay_type_salary' },
    { value: 'hourly', key: 'pay_type_hourly' },
    { value: 'hybrid', key: 'pay_type_hybrid' },
];
const PAY_TYPE_KEYS = { salary: 'pay_type_salary', hourly: 'pay_type_hourly', hybrid: 'pay_type_hybrid' };

const PAY_FREQUENCIES = [
    { value: 'monthly', key: 'pay_frequency_monthly' },
    { value: 'weekly', key: 'pay_frequency_weekly' },
    { value: 'daily', key: 'pay_frequency_daily' },
];
const PAY_FREQUENCY_KEYS = { monthly: 'pay_frequency_monthly', weekly: 'pay_frequency_weekly', daily: 'pay_frequency_daily' };

const TIP_PARTICIPATIONS = [
    { value: 'none', key: 'tip_participation_none' },
    { value: 'direct_only', key: 'tip_participation_direct_only' },
    { value: 'pool_member', key: 'tip_participation_pool_member' },
];
const TIP_PARTICIPATION_KEYS = { none: 'tip_participation_none', direct_only: 'tip_participation_direct_only', pool_member: 'tip_participation_pool_member' };

const DEPARTMENT_KEYS = ['kitchen', 'front_of_house', 'bar', 'management', 'delivery', 'other'];
const DEPARTMENT_VALUES = ['Kitchen', 'Front of House', 'Bar', 'Management', 'Delivery', 'Other'];

const FieldRow = ({ label, children }) => (
    <div>
        <label style={labelStyle}>{label}</label>
        <div style={valueStyle}>{children}</div>
    </div>
);

// ─── Employment card (core role/department + employment block) ───────────────

const EmploymentCard = ({ employee, t }) => {
    const e = employee.employment || {};
    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('employment')}</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <FieldRow label={t('role')}>{employee.role}</FieldRow>
                <FieldRow label={t('department')}>{employee.department || '—'}</FieldRow>
                <FieldRow label={t('employment_type')}>
                    {e.employment_type ? t(EMPLOYMENT_TYPE_KEYS[e.employment_type] || e.employment_type) : '—'}
                </FieldRow>
                <FieldRow label={t('start_date')}>{fmtDate(e.start_date)}</FieldRow>
                <FieldRow label={t('end_date')}>{fmtDate(e.end_date)}</FieldRow>
                <FieldRow label={t('termination_reason')}>{e.termination_reason || '—'}</FieldRow>
            </div>
        </div>
    );
};

// ─── Compensation Structure card ─────────────────────────────────────────────

const CompensationCard = ({ employee, currency, t }) => {
    const c = employee.compensation || {};
    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('compensation_structure')}</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <FieldRow label={t('pay_type')}>{t(PAY_TYPE_KEYS[c.pay_type] || 'pay_type_salary')}</FieldRow>
                <FieldRow label={t('base_amount')}>{fmtMoney(currency, c.base_amount)}</FieldRow>
                <FieldRow label={t('pay_frequency')}>{t(PAY_FREQUENCY_KEYS[c.pay_frequency] || 'pay_frequency_monthly')}</FieldRow>
                <FieldRow label={t('tip_participation')}>{t(TIP_PARTICIPATION_KEYS[c.tip_participation] || 'tip_participation_none')}</FieldRow>
                <FieldRow label={t('overtime_eligible')}>
                    {c.is_overtime_eligible ? `${t('yes')} · ×${Number(c.overtime_multiplier || 1.5).toFixed(2)}` : t('no')}
                </FieldRow>
                <FieldRow label={t('bonus_eligible')}>{c.is_bonus_eligible ? t('yes') : t('no')}</FieldRow>
            </div>
        </div>
    );
};

// ─── Salary change history ───────────────────────────────────────────────────

const HistoryCard = ({ employee, currency, t }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await employeeV2Service.getCompensationHistory(employee.employee_id);
            setHistory(data);
        } catch {
            /* swallow — show empty */
        } finally {
            setLoading(false);
        }
    }, [employee.employee_id]);

    useEffect(() => { load(); }, [load]);

    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('salary_change_history')}</h3>
            </div>

            {loading ? (
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '12px 0' }}>{t('loading')}</div>
            ) : history.length === 0 ? (
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '16px 0' }}>{t('no_history_yet')}</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {history.map(h => {
                        const oldAmount = h.old_values_json?.base_amount;
                        const newAmount = h.new_values_json?.base_amount;
                        const delta = (Number(newAmount) || 0) - (Number(oldAmount) || 0);
                        const isInitial = (h.reason || '') === 'initial';
                        return (
                            <div key={h.change_id} style={{ padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                        {isInitial
                                            ? `${t('initial')} · ${fmtMoney(currency, newAmount)}`
                                            : oldAmount !== undefined && newAmount !== undefined
                                                ? `${fmtMoney(currency, oldAmount)} → ${fmtMoney(currency, newAmount)}`
                                                : t('compensation_updated')}
                                    </div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                        {fmtDate(h.effective_date)} · {h.changed_by}{h.reason && !isInitial ? ` · ${h.reason}` : ''}
                                    </div>
                                </div>
                                {!isInitial && delta !== 0 && (
                                    <div style={{ fontSize: 12, fontWeight: 700, color: delta > 0 ? C.green : C.redText, whiteSpace: 'nowrap' }}>
                                        {delta > 0 ? '+' : ''}{fmtMoney(currency, delta)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Main export ──────────────────────────────────────────────────────────────

const TabEmployment = ({ employee, onRefresh, currency, t }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <EmploymentCard employee={employee} t={t} />
        <CompensationCard employee={employee} currency={currency} t={t} />
        <HistoryCard employee={employee} currency={currency} t={t} />
    </div>
);

export default TabEmployment;
